import {
  type MonthReport,
  type QuotesReport,
  type RankingRow,
  Settings as SettingsSchema,
  formatBRL,
  formatIsoDate,
  isoDateInSaoPaulo,
  monthLabel,
  monthOf,
} from '@deizi/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router';
import { AccountingPackForm } from '../../components/AccountingPack';
import { AffixInput, Field, Seg } from '../../components/Form';
import { Modal } from '../../components/Overlays';
import { PageHead } from '../../components/PageHead';
import { Alert, EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { centavosInput, formatHm, parseCentavos } from '../../lib/numbers';
import {
  reportsKey,
  useMonthReport,
  usePayback,
  useProduction,
  useQuotesReport,
  useRankings,
  useReportMonths,
  useRevenueLimit,
} from '../../lib/reports-api';
import { useIsAdmin } from '../../lib/session';

type Tab = 'mes' | 'retorno' | 'produtos' | 'producao' | 'orcamentos';
const TABS: { key: Tab; label: string }[] = [
  { key: 'mes', label: 'Mês' },
  { key: 'retorno', label: 'Retorno do investimento' },
  { key: 'produtos', label: 'Produtos e canais' },
  { key: 'producao', label: 'Produção' },
  { key: 'orcamentos', label: 'Orçamentos' },
];

const pct = (b: number | null, digits = 1) =>
  b === null
    ? '—'
    : `${(b / 100).toLocaleString('pt-BR', { maximumFractionDigits: digits, minimumFractionDigits: 0 })}%`;
const signedPct = (b: number | null) =>
  b === null ? '—' : `${b > 0 ? '+ ' : b < 0 ? '− ' : ''}${pct(Math.abs(b))}`;
const signedBRL = (c: number) => (c < 0 ? `− ${formatBRL(-c)}` : formatBRL(c));
const dm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const kg = (g: number) => `${(g / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kg`;
const MATERIAL_COLORS = ['#FF5C5C', '#0F5D6B', '#E8C547', '#7FB2BA', '#9A5B00', '#C9BFB5'];

export function ReportsPage() {
  const params = useParams();
  const tab: Tab = TABS.find((t) => t.key === params.tab)?.key ?? 'mes';
  const months = useReportMonths();
  const current = monthOf();
  const [month, setMonth] = useState(current);
  const [pack, setPack] = useState(false);
  const isAdmin = useIsAdmin();
  const today = isoDateInSaoPaulo();
  const options = months.data?.months ?? [current];
  return (
    <>
      <PageHead
        crumb="Dinheiro"
        title="Relatórios"
        actions={
          <div className="row wrap" style={{ gap: 10 }}>
            <select
              className="input"
              aria-label="Período"
              style={{ width: 240 }}
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              disabled={tab === 'retorno'}
            >
              {options.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                  {m === current ? ` · até dia ${Number(today.slice(8, 10))}` : ''}
                </option>
              ))}
            </select>
            {isAdmin ? (
              <button type="button" className="btn" onClick={() => setPack(true)}>
                Pacote do contador
              </button>
            ) : null}
          </div>
        }
      />
      <nav className="tabs" aria-label="Relatórios">
        {TABS.map((t) => (
          <Link
            key={t.key}
            to={t.key === 'mes' ? '/relatorios' : `/relatorios/${t.key}`}
            className={tab === t.key ? 'on' : ''}
            aria-current={tab === t.key ? 'page' : undefined}
          >
            {t.label}
          </Link>
        ))}
      </nav>
      {pack ? (
        <Modal title="Pacote do contador" onClose={() => setPack(false)}>
          <AccountingPackForm initialMonth={month} />
        </Modal>
      ) : null}
      {tab === 'mes' ? <MonthTab month={month} /> : null}
      {tab === 'retorno' ? <PaybackTab /> : null}
      {tab === 'produtos' ? <RankingsTab month={month} /> : null}
      {tab === 'producao' ? <ProductionTab month={month} /> : null}
      {tab === 'orcamentos' ? <QuotesTab month={month} /> : null}
    </>
  );
}

function Loading<T>({
  q,
  children,
}: {
  q: { isPending: boolean; isError: boolean; data: T | undefined; refetch: () => unknown };
  children: (data: T) => ReactNode;
}) {
  if (q.isPending) return <Skeleton rows={4} height={64} />;
  if (q.isError || !q.data)
    return (
      <div className="card">
        <ErrorBox
          title="Não foi possível carregar os relatórios"
          onRetry={() => void q.refetch()}
        />
      </div>
    );
  return <>{children(q.data)}</>;
}

function Stat({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  color?: string;
}) {
  return (
    <div>
      <p className="xs muted">{label}</p>
      <p className="fr tab" style={{ fontSize: 24, color }}>
        {value}
      </p>
      {sub ? <p className="xs muted">{sub}</p> : null}
    </div>
  );
}

// ---- Mês ----------------------------------------------------------------------------------------

function MonthTab({ month }: { month: string }) {
  const q = useMonthReport(month);
  return <Loading q={q}>{(r) => <MonthView r={r} />}</Loading>;
}

function MonthView({ r }: { r: MonthReport }) {
  const name = monthLabel(r.month, { year: false, capital: false });
  const be = r.breakEven;
  const resultColor = r.netProfitCentavos < 0 ? 'var(--err)' : 'var(--ok)';
  const fill = be.fillBps / 100;
  const missing = Math.max(0, r.fixed.totalCentavos - r.contributionCentavos);
  const estimates = r.fixed.items.some((i) => i.estimate);
  // Prints outside orders get a row of their own; the rest are the usual fixed costs.
  const prints = r.fixed.items.find((i) => i.source === 'prints');
  const fixedItems = r.fixed.items.filter((i) => i.source !== 'prints');
  const fixedLabels = [...new Set(fixedItems.map((i) => `${i.label}${i.estimate ? '*' : ''}`))];
  const cashOk = r.cash.balanceCentavos >= 0;
  const coverage = r.cash.coverageBps;
  return (
    <div className="col" style={{ gap: 22 }}>
      {r.orders === 0 ? (
        <div className="card">
          <EmptyState
            title={`Nenhuma venda em ${name} ainda`}
            action={
              <Link className="btn p" to="/orcamento-rapido">
                Fazer orçamento rápido
              </Link>
            }
          >
            O ponto de equilíbrio aparece com o primeiro pedido do mês. Custos fixos e parcelas já
            estão abaixo.
          </EmptyState>
        </div>
      ) : (
        <section className="card col" style={{ gap: 16 }}>
          <div className="row between wrap" style={{ alignItems: 'flex-end', gap: 16 }}>
            <div>
              <p className="lbl">
                Ponto de equilíbrio de {name}
                {r.partial ? (
                  <span className="badge" style={{ marginLeft: 8 }}>
                    parcial
                  </span>
                ) : null}
              </p>
              <p className="big">
                {formatBRL(r.contributionCentavos)}{' '}
                <span className="muted" style={{ fontSize: 22 }}>
                  de {formatBRL(r.fixed.totalCentavos)}
                </span>
              </p>
              <p className="sm muted">margem de contribuição acumulada vs. custos fixos do mês</p>
            </div>
            <div className="row wrap" style={{ gap: 28 }}>
              <Stat
                label="Unidades vendidas"
                value={
                  <>
                    {r.units}{' '}
                    {!be.reached && be.unitsNeeded !== null ? (
                      <span className="sm muted" style={{ fontFamily: 'Outfit' }}>
                        de {be.unitsNeeded}
                      </span>
                    ) : null}
                  </>
                }
              />
              {be.reached ? (
                <Stat label="Atingido em" value={be.reachedOn ? dm(be.reachedOn) : '—'} />
              ) : (
                <Stat
                  label="Faltam"
                  value={
                    r.daysLeft > 0
                      ? `${r.daysLeft} ${r.daysLeft === 1 ? 'dia' : 'dias'}`
                      : 'mês fechado'
                  }
                />
              )}
              <Stat
                label="Resultado do mês"
                value={signedBRL(r.netProfitCentavos)}
                color={resultColor}
              />
            </div>
          </div>
          <div className="layers" style={{ height: 22, marginTop: 12 }}>
            <div
              className="fill"
              style={{ width: `${fill}%`, borderRadius: fill >= 100 ? 6 : undefined }}
            />
            <div
              className="nozzle"
              style={{ left: fill >= 100 ? 'calc(100% - 8px)' : `${fill}%` }}
            />
          </div>
          {r.fixed.totalCentavos === 0 ? (
            <div className="alert info">
              <div>
                <b>Nenhum custo fixo neste mês.</b> Lance o DAS, internet e anúncios em Despesas
                para o ponto de equilíbrio valer.
              </div>
            </div>
          ) : be.reached ? (
            <div className="alert ok">
              <div>
                <b>
                  {be.timesFixed !== null
                    ? `${be.timesFixed.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}× os custos fixos.`
                    : 'Custos fixos cobertos.'}
                </b>{' '}
                Tudo depois de {be.reachedOn ? dm(be.reachedOn) : 'agora'} foi lucro.
              </div>
            </div>
          ) : (
            <div className="alert warn">
              <div>
                <b>
                  {be.unitsMissing !== null
                    ? `Faltam ${be.unitsMissing} ${be.unitsMissing === 1 ? 'unidade' : 'unidades'} (≈ ${formatBRL(missing)} de contribuição)${r.daysLeft > 0 ? ` em ${r.daysLeft} dias` : ''}.`
                    : `Faltam ${formatBRL(missing)} de contribuição.`}
                </b>{' '}
                {be.perUnitCentavos !== null
                  ? `Média de ${formatBRL(be.perUnitCentavos)} por unidade vendida${be.perOrderCentavos !== null ? ` (${formatBRL(be.perOrderCentavos)} por pedido)` : ''}${be.lowestChannel ? `; ${be.lowestChannel.name} está puxando a média para baixo (${pct(be.lowestChannel.marginBps)}).` : '.'}`
                  : ''}
              </div>
            </div>
          )}
        </section>
      )}
      <div className="two">
        <section className="card flat">
          <div style={{ padding: '16px 18px 6px' }}>
            <h2>Resultado do mês</h2>
            {r.openOrders ? (
              <span className="xs muted">
                {r.openOrders} {r.openOrders === 1 ? 'pedido em aberto' : 'pedidos em aberto'}:
                custo estimado até imprimir
              </span>
            ) : null}
          </div>
          <div className="gt">
            {r.rows.map((row) => (
              <div key={row.key} className="gtr" style={{ gridTemplateColumns: '1fr auto' }}>
                <span className={row.key === 'revenue' ? '' : 'muted'}>{row.label}</span>
                <span className="num">{signedBRL(row.amountCentavos)}</span>
              </div>
            ))}
            <div className="gtr foot" style={{ gridTemplateColumns: '1fr auto' }}>
              <span>Margem de contribuição</span>
              <span className="num">{signedBRL(r.contributionCentavos)}</span>
            </div>
            <div className="gtr" style={{ gridTemplateColumns: '1fr auto' }}>
              <span className="muted">
                Custos fixos{fixedLabels.length ? ` (${fixedLabels.join(', ')})` : ''}
              </span>
              <span className="num">
                {signedBRL(-(r.fixed.totalCentavos - (prints?.amountCentavos ?? 0)))}
              </span>
            </div>
            {prints ? (
              <div className="gtr" style={{ gridTemplateColumns: '1fr auto' }}>
                <span className="muted">
                  <Link to={`/fila?avulsas=${r.month}`}>
                    Impressões fora de pedido (testes, uso próprio, presentes)
                  </Link>
                </span>
                <span className="num">{signedBRL(-prints.amountCentavos)}</span>
              </div>
            ) : null}
            <div className="gtr foot" style={{ gridTemplateColumns: '1fr auto' }}>
              <span>Lucro líquido</span>
              <span className="num" style={{ color: resultColor }}>
                {signedBRL(r.netProfitCentavos)}
              </span>
            </div>
          </div>
          <p className="xs muted" style={{ padding: '10px 18px 16px' }}>
            {estimates ? '*Valor estimado (a lançar): confirme no boleto em Despesas. ' : ''}
            Depreciação das impressoras já está no custo de cada peça e não entra de novo aqui.
          </p>
        </section>
        <section className="card col" style={{ gap: 12 }}>
          <div className="row between">
            <h2>Caixa de {name}</h2>
            {cashOk ? (
              <span className="badge ok">Caixa positivo</span>
            ) : (
              <span className="badge err">
                {r.cash.installmentsCentavos > 0 ? 'Vendas não cobrem a parcela' : 'Caixa negativo'}
              </span>
            )}
          </div>
          <div className="kv">
            <span>Entrou das vendas (contribuição + depreciação recebida)</span>
            <span className="v">{signedBRL(r.cash.inCentavos)}</span>
          </div>
          {r.cash.installments.length ? (
            r.cash.installments.map((i) => (
              <div key={`${i.label}-${i.dueOn}`} className="kv">
                <span>
                  {i.label} · {dm(i.dueOn)}
                  {i.paid ? (
                    <span className="badge ok" style={{ marginLeft: 6 }}>
                      paga
                    </span>
                  ) : null}
                </span>
                <span className="v">− {formatBRL(i.amountCentavos)}</span>
              </div>
            ))
          ) : (
            <div className="kv">
              <span>Nenhuma parcela em {name}</span>
              <span className="v">{formatBRL(0)}</span>
            </div>
          )}
          <div className="kv">
            <span>Custos fixos do mês</span>
            <span className="v">− {formatBRL(r.cash.fixedCentavos)}</span>
          </div>
          <div className="kv" style={{ borderBottom: 0 }}>
            <span style={{ fontWeight: 600 }}>Saldo de caixa do mês</span>
            <span
              className="v fr"
              style={{ fontSize: 20, color: cashOk ? 'var(--ok)' : 'var(--err)' }}
            >
              {signedBRL(r.cash.balanceCentavos)}
            </span>
          </div>
          {coverage !== null ? (
            <>
              <div
                className="meter"
                aria-label={`Vendas cobrem ${pct(Math.min(10_000, coverage), 0)} das parcelas`}
              >
                <span
                  style={{
                    width: `${Math.max(1, Math.min(100, coverage / 100))}%`,
                    background: coverage >= 10_000 ? 'var(--ok)' : 'var(--co)',
                  }}
                />
              </div>
              <p className="xs muted">
                Vendas cobrem {pct(Math.max(0, coverage), 0)} das parcelas de {name}.
              </p>
            </>
          ) : null}
          {r.cash.missingPayout.length ? (
            <p className="xs muted">
              Sem prazo de recebimento: {r.cash.missingPayout.join(', ')} (contado na data da
              venda). Defina em <Link to="/configuracoes/canais">Canais</Link>.
            </p>
          ) : null}
          <p className="sm muted">
            Parcela é caixa; depreciação é custo. Esta visão mostra só se o dinheiro do mês fecha,
            sem somar os dois no lucro.
          </p>
          <div className="gt" style={{ marginTop: 4 }}>
            <div className="gtr head" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <span>Próximos meses</span>
              <span className="num">Parcelas</span>
              <span className="num">Resultado</span>
            </div>
            {r.schedule.map((s) => (
              <div key={s.month} className="gtr" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <span className="sm">{monthLabel(s.month)}</span>
                <span className="num sm">
                  {s.installmentsCentavos ? `− ${formatBRL(s.installmentsCentavos)}` : '—'}
                </span>
                <span
                  className="num sm"
                  style={{
                    color:
                      s.netProfitCentavos === null
                        ? undefined
                        : s.netProfitCentavos < 0
                          ? 'var(--err)'
                          : 'var(--ok)',
                  }}
                >
                  {s.netProfitCentavos === null ? '—' : signedBRL(s.netProfitCentavos)}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
      <div className="row wrap" style={{ gap: 10 }}>
        <Link className="btn sm" to="/relatorios/produtos">
          Lucro por produto e canal
        </Link>
        <Link className="btn sm" to="/relatorios/producao">
          Produção do mês
        </Link>
        <Link className="btn sm g" to="/despesas">
          Ver despesas de {name}
        </Link>
      </div>
    </div>
  );
}

// ---- Retorno -----------------------------------------------------------------------------------

function PaybackTab() {
  const q = usePayback();
  const year = Number(monthOf().slice(0, 4));
  const limit = useRevenueLimit(year);
  const isAdmin = useIsAdmin();
  const [editing, setEditing] = useState(false);
  return (
    <div className="col" style={{ gap: 22 }}>
      <Loading q={q}>
        {(p) => {
          // The last real months, then the projection sampled down to the payback month.
          const real = p.months.filter((b) => !b.projected).slice(-4);
          const projected = p.months.filter((b) => b.projected);
          const room = 13 - real.length;
          const step = Math.max(1, Math.ceil(projected.length / room));
          const sampled = projected.filter(
            (_, i) => (i + 1) % step === 0 || i === projected.length - 1,
          );
          const bars = [...real, ...sampled];
          const top = Math.max(p.investmentCentavos, ...bars.map((b) => b.cumulativeCentavos), 1);
          const today = isoDateInSaoPaulo();
          return (
            <section className="card col" style={{ gap: 14 }}>
              <div className="row between wrap">
                <h2>Retorno do investimento</h2>
                <span className="sm muted">
                  {p.equipment.map((e) => e.name).join(' + ') || 'Sem equipamentos'} ·{' '}
                  {formatBRL(p.investmentCentavos)}
                </span>
              </div>
              <div className="row wrap" style={{ gap: 28 }}>
                <Stat
                  label={`Recuperado até ${dm(today)}`}
                  value={
                    <>
                      {signedBRL(p.recoveredCentavos)}{' '}
                      <span className="sm muted" style={{ fontFamily: 'Outfit' }}>
                        {pct(p.recoveredBps)}
                      </span>
                    </>
                  }
                />
                <Stat label="Falta recuperar" value={formatBRL(p.remainingCentavos)} />
                <Stat
                  label="Projeção de retorno"
                  value={
                    p.paybackMonth
                      ? `${monthLabel(p.paybackMonth, { year: false, capital: false })} de ${p.paybackMonth.slice(0, 4)}`
                      : 'sem projeção'
                  }
                  sub={
                    p.paceCentavos !== null && p.paceCentavos > 0
                      ? `no ritmo dos últimos meses: ≈ ${formatBRL(p.paceCentavos)}/mês`
                      : 'ainda sem meses que recuperem o investimento'
                  }
                />
              </div>
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-end',
                  paddingTop: 14,
                }}
                role="img"
                aria-label="Valor recuperado acumulado por mês, real e projeção"
              >
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 14 + 34 + (1 - p.investmentCentavos / top) * 150,
                    borderTop: '2px dashed var(--pe)',
                  }}
                />
                <span
                  className="xs"
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: -6,
                    color: 'var(--pe)',
                    fontWeight: 500,
                  }}
                >
                  investimento {formatBRL(p.investmentCentavos)}
                </span>
                {bars.map((b) => {
                  const h = Math.max(
                    2,
                    Math.round((Math.max(0, b.cumulativeCentavos) / top) * 100),
                  );
                  const last = b.month === p.paybackMonth;
                  return (
                    <div key={b.month} className="pb" style={{ height: 'auto', gap: 4 }}>
                      <span
                        className="xs tab"
                        style={{
                          height: 30,
                          display: 'flex',
                          alignItems: 'flex-end',
                          textAlign: 'center',
                          fontWeight: 600,
                          color: 'var(--ink)',
                        }}
                      >
                        {!b.projected || last
                          ? `${b.projected ? '≈ ' : ''}${formatBRL(b.cumulativeCentavos).replace(',00', '')}`
                          : ''}
                      </span>
                      <div
                        style={{
                          height: 150,
                          width: '100%',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                        }}
                      >
                        <i
                          style={{
                            height: `${h}%`,
                            background: b.projected ? '#FFD9D3' : 'var(--co)',
                            outline: last ? '2px solid var(--pe)' : undefined,
                            outlineOffset: 1,
                          }}
                        />
                      </div>
                      <span className={last ? 'xs' : 'xs muted'}>
                        {monthLabel(b.month, { year: false, capital: false }).slice(0, 3)}
                        {b.month.slice(5) === '01' ||
                        (last && b.month.slice(0, 4) !== monthOf().slice(0, 4))
                          ? `/${b.month.slice(2, 4)}`
                          : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="col" style={{ gap: 6 }}>
                <p className="lbl">Como ler</p>
                <div className="legend" style={{ flexDirection: 'column', gap: 6 }}>
                  <span>
                    <i className="sw" style={{ background: '#FF5C5C' }} />
                    <b style={{ fontWeight: 600, color: 'var(--ink)' }}>Real</b> · lucro líquido +
                    depreciação recebida nas vendas, acumulado
                  </span>
                  <span>
                    <i className="sw" style={{ background: '#FFD9D3' }} />
                    <b style={{ fontWeight: 600, color: 'var(--ink)' }}>Projeção</b> · média dos
                    últimos meses com vendas
                  </span>
                  <span>
                    <i
                      className="sw"
                      style={{ border: '2px dashed #0F5D6B', background: 'transparent' }}
                    />
                    <b style={{ fontWeight: 600, color: 'var(--ink)' }}>Investimento</b> · linha
                    tracejada; a barra com contorno é o mês em que ela é alcançada
                  </span>
                </div>
              </div>
            </section>
          );
        }}
      </Loading>
      <Loading q={limit}>
        {(l) => {
          const used = l.usedBps / 100;
          const color = l.alert ? 'var(--warn)' : 'var(--pe)';
          return (
            <section className="card col" style={{ gap: 12 }}>
              <div className="row between wrap">
                <h2>Limite de faturamento · MEI</h2>
                {isAdmin ? (
                  <button type="button" className="btn sm" onClick={() => setEditing(true)}>
                    Editar limite
                  </button>
                ) : null}
              </div>
              <div className="row wrap" style={{ gap: 28 }}>
                <Stat label={`Faturado em ${l.year}`} value={formatBRL(l.revenueCentavos)} />
                <Stat label="Limite anual" value={formatBRL(l.limitCentavos)} />
                <Stat label="Usado" value={pct(l.usedBps)} color={color} />
                <Stat label="Alerta em 80%" value={formatBRL(l.alertAtCentavos)} />
              </div>
              <div style={{ position: 'relative', marginTop: 6 }}>
                <div className="meter" style={{ height: 14 }}>
                  <span
                    style={{ width: `${Math.min(100, Math.max(0.5, used))}%`, background: color }}
                  />
                </div>
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: '80%',
                    top: -4,
                    bottom: -4,
                    borderLeft: '2px solid #9A5B00',
                  }}
                />
              </div>
              <div className="row between xs muted">
                <span>R$ 0</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    marginRight: '18%',
                    color: 'var(--warn)',
                    fontWeight: 500,
                  }}
                >
                  80% · alerta
                </span>
                <span>{formatBRL(l.limitCentavos)}</span>
              </div>
              {l.alert ? (
                <div className="alert warn">
                  <div>
                    <b>Passou de 80% do limite.</b> Converse com o contador sobre migrar para ME
                    antes de estourar.
                  </div>
                </div>
              ) : null}
              <div className="card sunk col" style={{ gap: 6, padding: 14 }}>
                <h3>Por que o alerta em 80%?</h3>
                <p className="sm">
                  Se o faturamento do ano passar do limite em até 20%, o MEI paga a diferença e sai
                  do regime no ano seguinte. Acima de 20%, o desenquadramento volta a janeiro e os
                  impostos são recalculados. Avisar em 80% ({formatBRL(l.alertAtCentavos)}) dá
                  alguns meses para planejar com o contador.
                </p>
                <p className="xs muted">
                  Há propostas para subir o teto; se mudar, ajuste aqui em “Editar limite”.
                  Faturamento pela data da venda, sem os reembolsos.
                </p>
              </div>
              {editing ? (
                <LimitDialog
                  current={l.limitCentavos}
                  invoiced={l.revenueCentavos}
                  onClose={() => setEditing(false)}
                />
              ) : null}
            </section>
          );
        }}
      </Loading>
    </div>
  );
}

function LimitDialog({
  current,
  invoiced,
  onClose,
}: {
  current: number;
  invoiced: number;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [value, setValue] = useState(centavosInput(current));
  const v = parseCentavos(value);
  const ok = v !== null && v > invoiced;
  const save = useMutation({
    mutationFn: () =>
      api.patch('/settings', SettingsSchema, {
        rules: { annualRevenueLimitCentavos: v },
        reason: 'Limite anual do MEI',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportsKey });
      void qc.invalidateQueries({ queryKey: ['settings'] });
      toast(`Limite atualizado para ${formatBRL(v ?? 0)}`);
      onClose();
    },
  });
  return (
    <Modal
      title="Limite anual do MEI"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn g"
            style={{ marginRight: 'auto' }}
            onClick={() => setValue(centavosInput(8_100_000))}
          >
            Voltar ao padrão
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={!ok || save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar limite
          </button>
        </>
      }
    >
      <Field
        id="lim-value"
        label="Limite de faturamento por ano"
        error={
          v !== null && !ok
            ? `Informe um valor acima do já faturado (${formatBRL(invoiced)}).`
            : null
        }
        help="Hoje: R$ 81.000,00. Em ano de abertura, o limite é proporcional aos meses (R$ 6.750,00 por mês)."
      >
        <AffixInput id="lim-value" prefix="R$" value={value} onChange={setValue} invalid={!ok} />
      </Field>
      <div className="kv sm">
        <span className="muted">Alerta em 80%</span>
        <span className="v">{v ? formatBRL(Math.round(v * 0.8)) : '—'}</span>
      </div>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
    </Modal>
  );
}

// ---- Produtos e canais ----------------------------------------------------------------------

function RankTable({
  title,
  sub,
  rows,
  sort,
  first,
  units = true,
}: {
  title: string;
  sub?: string;
  rows: RankingRow[];
  sort: 'lucro' | 'margem';
  first: string;
  units?: boolean;
}) {
  const sorted =
    sort === 'lucro' ? rows : rows.toSorted((a, b) => (b.marginBps ?? -1) - (a.marginBps ?? -1));
  const cols = units ? '40px 2fr 70px 1fr 1fr 1fr' : '40px 2fr 1fr 1fr 1fr';
  return (
    <section className="card flat">
      <div className="row between" style={{ padding: '16px 18px 6px' }}>
        <h2>{title}</h2>
        {sub ? <span className="sm muted">{sub}</span> : null}
      </div>
      <div className="gt">
        <div className="gtr head" style={{ gridTemplateColumns: cols }}>
          <span>#</span>
          <span>{first}</span>
          {units ? <span className="num">Un.</span> : null}
          <span className="num">Vendas</span>
          <span className="num">Lucro</span>
          <span className="num">Margem</span>
        </div>
        {sorted.length === 0 ? (
          <p className="sm muted" style={{ padding: '12px 18px' }}>
            Sem vendas no mês.
          </p>
        ) : null}
        {sorted.map((r, i) => {
          const low = r.marginBps !== null && r.marginBps < 1500;
          const content = (
            <>
              <span className={i === 0 && r.salesCentavos > 0 ? 'rank first' : 'rank'}>
                {i + 1}
              </span>
              <span style={{ fontWeight: 500 }}>
                {r.color ? (
                  <span className="dot" style={{ background: r.color, marginRight: 8 }} />
                ) : null}
                {r.name}
              </span>
              {units ? <span className="num">{r.units}</span> : null}
              <span className="num">{formatBRL(r.salesCentavos)}</span>
              <span className="num" style={{ fontWeight: 600 }}>
                {signedBRL(r.profitCentavos)}
              </span>
              <span
                className="num"
                style={{
                  color: low ? 'var(--warn)' : undefined,
                  fontWeight: low ? 600 : undefined,
                }}
              >
                {pct(r.marginBps)}
              </span>
            </>
          );
          return r.link ? (
            <Link
              key={r.key}
              className={low ? 'gtr wl' : 'gtr'}
              to={r.link}
              style={{ gridTemplateColumns: cols }}
            >
              {content}
            </Link>
          ) : (
            <div
              key={r.key}
              className={low ? 'gtr wl' : 'gtr'}
              style={{ gridTemplateColumns: cols }}
            >
              {content}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RankingsTab({ month }: { month: string }) {
  const q = useRankings(month);
  const [sort, setSort] = useState<'lucro' | 'margem'>('lucro');
  const name = monthLabel(month, { year: false });
  return (
    <Loading q={q}>
      {(r) => (
        <div className="col" style={{ gap: 18 }}>
          <div className="row between wrap" style={{ gap: 12 }}>
            <p className="sm muted">
              {name} · ranking por {sort === 'lucro' ? 'lucro' : 'margem real'}. Lucro =
              contribuição antes dos custos fixos; margem real = lucro ÷ vendas, depois de comissões
              e tarifas.
            </p>
            <Seg
              label="Ordenar por"
              value={sort}
              onChange={setSort}
              options={[
                { value: 'lucro', label: 'Lucro' },
                { value: 'margem', label: 'Margem' },
              ]}
            />
          </div>
          <RankTable
            title="Lucro por produto"
            sub="abaixo de 15% em amarelo"
            rows={r.products}
            sort={sort}
            first="Produto"
          />
          <div className="two">
            <RankTable
              title="Lucro por canal"
              rows={r.channels}
              sort={sort}
              first="Canal"
              units={false}
            />
            <RankTable title="Lucro por material" rows={r.materials} sort={sort} first="Material" />
          </div>
          <p className="xs muted">
            Totais de {monthLabel(month, { year: false, capital: false })}: vendas{' '}
            {formatBRL(r.revenueCentavos)} · contribuição {signedBRL(r.contributionCentavos)}.
          </p>
        </div>
      )}
    </Loading>
  );
}

// ---- Produção ----------------------------------------------------------------------------------

function ProductionTab({ month }: { month: string }) {
  const q = useProduction(month);
  const name = monthLabel(month, { year: false, capital: false });
  return (
    <Loading q={q}>
      {(p) => {
        const materials = [
          ...new Set(p.byMonth.flatMap((m) => m.materials.map((x) => x.material))),
        ];
        const color = (m: string) =>
          MATERIAL_COLORS[materials.indexOf(m) % MATERIAL_COLORS.length]!;
        const max = Math.max(
          1,
          ...p.byMonth.map((m) => m.materials.reduce((n, x) => n + x.grams, 0)),
        );
        const maxReason = Math.max(1, ...p.reasons.map((r) => r.count));
        const failed = p.reasons.reduce((n, r) => n + r.count, 0);
        const est = [
          {
            k: 'Tempo de impressão',
            a: p.estimate.estSeconds,
            b: p.estimate.realSeconds,
            fmt: (s: number) => formatHm(s),
          },
          {
            k: 'Filamento',
            a: p.estimate.estGrams,
            b: p.estimate.realGrams,
            fmt: (g: number) => `${Math.round(g).toLocaleString('pt-BR')} g`,
          },
        ];
        return (
          <div className="col" style={{ gap: 18 }}>
            <div className="grid s">
              <div className="card">
                <p className="xs muted">Filamento usado · {name}</p>
                <p className="fr tab" style={{ fontSize: 24 }}>
                  {kg(p.gramsUsed)}
                </p>
              </div>
              <div className="card">
                <p className="xs muted">Taxa de falha</p>
                <p className="fr tab" style={{ fontSize: 24 }}>
                  {pct(p.failureBps)}
                </p>
                <p className="xs muted">por horas impressas</p>
              </div>
              <div className="card">
                <p className="xs muted">Tempo real vs. estimado</p>
                <p className="fr tab" style={{ fontSize: 24 }}>
                  {signedPct(p.timeDeltaBps)}
                </p>
              </div>
              <div className="card">
                <p className="xs muted">Gramas reais vs. estimadas</p>
                <p className="fr tab" style={{ fontSize: 24 }}>
                  {signedPct(p.gramsDeltaBps)}
                </p>
              </div>
            </div>
            <div className="two">
              <section className="card col" style={{ gap: 12 }}>
                <div className="row between">
                  <h2>Filamento por mês</h2>
                  <span className="sm muted">kg</span>
                </div>
                <div
                  className="bars"
                  style={{ height: 180 }}
                  role="img"
                  aria-label="Filamento por mês"
                >
                  {p.byMonth.map((m) => {
                    const total = m.materials.reduce((n, x) => n + x.grams, 0);
                    const sel = m.month === month;
                    return (
                      <div key={m.month} className="b" style={{ gap: 0 }}>
                        <p className="xs tab" style={{ fontWeight: 600, marginBottom: 6 }}>
                          {total
                            ? (total / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })
                            : ''}
                        </p>
                        {m.materials.toReversed().map((x) => (
                          <span
                            key={x.material}
                            style={{
                              height: `${(x.grams / max) * 85}%`,
                              background: color(x.material),
                              maxWidth: 56,
                              opacity: sel ? 1 : 0.45,
                            }}
                          />
                        ))}
                        <p className={sel ? 'xs' : 'xs muted'} style={{ marginTop: 6 }}>
                          {monthLabel(m.month, { year: false, capital: false }).slice(0, 3)}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <div className="legend">
                  {materials.map((m) => (
                    <span key={m}>
                      <i className="sw" style={{ background: color(m) }} />
                      {m}
                    </span>
                  ))}
                  {materials.length === 0 ? <span>Nenhuma impressão concluída ainda.</span> : null}
                </div>
              </section>
              <section className="card flat">
                <div style={{ padding: '16px 18px 6px' }}>
                  <h2>Falhas por impressora e material</h2>
                </div>
                <div className="gt">
                  <div className="gtr head" style={{ gridTemplateColumns: '1fr 1fr 70px 1.4fr' }}>
                    <span>Impressora</span>
                    <span>Material</span>
                    <span className="num">Falhas</span>
                    <span>Taxa</span>
                  </div>
                  {p.failures.length === 0 ? (
                    <p className="sm muted" style={{ padding: '12px 18px' }}>
                      Nenhuma impressão no mês.
                    </p>
                  ) : null}
                  {p.failures.map((f) => (
                    <Link
                      key={`${f.printerId}-${f.material}`}
                      className="gtr"
                      to="/impressoras"
                      style={{ gridTemplateColumns: '1fr 1fr 70px 1.4fr' }}
                    >
                      <span>{f.printer}</span>
                      <span>{f.material}</span>
                      <span className="num">
                        {f.failed} de {f.jobs}
                      </span>
                      <span className="row" style={{ gap: 8 }}>
                        <span className="meter grow" style={{ height: 8 }}>
                          <span
                            style={{
                              width: `${Math.min(100, f.failureBps / 10)}%`,
                              background: f.failureBps > 500 ? 'var(--err)' : 'var(--pe)',
                            }}
                          />
                        </span>
                        <span className="tab sm" style={{ width: 44, textAlign: 'right' }}>
                          {pct(f.failureBps)}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            </div>
            <div className="two">
              <section className="card col" style={{ gap: 12 }}>
                <h2>Estimado vs. real</h2>
                {est.map((e) => {
                  const top = Math.max(e.a, e.b, 1);
                  const delta = e.a > 0 ? Math.round(((e.b - e.a) / e.a) * 10_000) : null;
                  return (
                    <div key={e.k} className="col" style={{ gap: 6 }}>
                      <div className="row between sm">
                        <span style={{ fontWeight: 500 }}>{e.k}</span>
                        <span
                          className={
                            delta !== null && Math.abs(delta) > 500 ? 'badge warn' : 'badge'
                          }
                        >
                          {signedPct(delta)}
                        </span>
                      </div>
                      {[
                        { l: 'estimado', v: e.a, c: '#7FB2BA' },
                        { l: 'real', v: e.b, c: '#0F5D6B' },
                      ].map((x) => (
                        <div key={x.l} className="row sm" style={{ gap: 10 }}>
                          <span className="muted" style={{ width: 70 }}>
                            {x.l}
                          </span>
                          <span className="grow">
                            <span
                              className="hbar"
                              style={{ width: `${(x.v / top) * 100}%`, background: x.c }}
                            />
                          </span>
                          <span className="tab" style={{ width: 80, textAlign: 'right' }}>
                            {e.fmt(x.v)}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })}
                <p className="xs muted">
                  Só impressões concluídas. Se o real passar de 5% do estimado por 2 meses, revise o
                  perfil de fatiamento ou o custo/hora.
                </p>
              </section>
              <section className="card col" style={{ gap: 12 }}>
                <div className="row between">
                  <h2>Motivos das falhas</h2>
                  <span className="sm muted">
                    {failed} {failed === 1 ? 'falha' : 'falhas'}
                  </span>
                </div>
                {p.reasons.length === 0 ? <p className="sm muted">Nenhuma falha no mês.</p> : null}
                {p.reasons.map((r) => (
                  <div key={r.reason} className="row sm" style={{ gap: 10 }}>
                    <span style={{ width: 150 }}>{r.label}</span>
                    <span className="grow">
                      <span
                        className="hbar"
                        style={{
                          width: `${(r.count / maxReason) * 100}%`,
                          background: 'var(--co)',
                        }}
                      />
                    </span>
                    <span
                      className="tab"
                      style={{ width: 24, textAlign: 'right', fontWeight: 600 }}
                    >
                      {r.count}
                    </span>
                  </div>
                ))}
                {p.lastFailure ? (
                  <p className="xs muted">
                    {p.lastFailure.number} falhou em {p.lastFailure.pct}% por{' '}
                    {p.lastFailure.reason.toLowerCase()} em {dm(p.lastFailure.on)}.
                  </p>
                ) : null}
                <Link
                  className="btn sm"
                  to={`/fila?falhas=${month}`}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Ver impressões com falha
                </Link>
              </section>
            </div>
          </div>
        );
      }}
    </Loading>
  );
}

// ---- Orçamentos ---------------------------------------------------------------------------------

const FUNNEL_COLORS = {
  accepted: '#27704A',
  declined: '#B42318',
  expired: '#C9BFB5',
  open: '#7FB2BA',
};

function FunnelRows({ title, rows }: { title: string; rows: QuotesReport['byChannel'] }) {
  const max = Math.max(1, ...rows.map((r) => r.sent));
  return (
    <section className="card col" style={{ gap: 14 }}>
      <h2>{title}</h2>
      {rows.every((r) => r.sent === 0) ? (
        <p className="sm muted">Nenhum orçamento enviado.</p>
      ) : null}
      {rows
        .filter((r) => r.sent > 0)
        .map((r) => (
          <div key={r.key} className="col" style={{ gap: 6 }}>
            <div className="row between sm">
              <span style={{ fontWeight: 500 }}>{r.key}</span>
              <span className="muted">
                {r.sent} {r.sent === 1 ? 'enviado' : 'enviados'} ·{' '}
                {pct(Math.round((r.accepted / r.sent) * 10_000), 0)} de conversão
              </span>
            </div>
            <div className="stack" style={{ width: `${Math.max(8, (r.sent / max) * 100)}%` }}>
              {(['accepted', 'declined', 'expired', 'open'] as const).map((k) => (
                <span key={k} style={{ flex: r[k], background: FUNNEL_COLORS[k] }} />
              ))}
            </div>
            <p className="xs muted">
              {[
                r.accepted ? `${r.accepted} ${r.accepted === 1 ? 'aceito' : 'aceitos'}` : null,
                r.declined ? `${r.declined} ${r.declined === 1 ? 'recusado' : 'recusados'}` : null,
                r.expired ? `${r.expired} ${r.expired === 1 ? 'expirado' : 'expirados'}` : null,
                r.open ? `${r.open} aguardando` : null,
              ]
                .filter(Boolean)
                .join(' · ') || 'nenhum'}
            </p>
          </div>
        ))}
    </section>
  );
}

function QuotesTab({ month }: { month: string }) {
  const q = useQuotesReport(month);
  return (
    <Loading q={q}>
      {(r) => (
        <div className="col" style={{ gap: 18 }}>
          <div className="grid s">
            <div className="card">
              <p className="xs muted">Enviados</p>
              <p className="fr tab" style={{ fontSize: 26 }}>
                {r.totals.sent}
              </p>
              <p className="xs muted">
                {!r.firstNumber
                  ? 'nenhum no mês'
                  : r.firstNumber === r.lastNumber
                    ? r.firstNumber
                    : `${r.firstNumber} a ${r.lastNumber?.slice(-4)}`}
              </p>
            </div>
            <div className="card">
              <p className="xs muted">Aceitos</p>
              <p className="fr tab" style={{ fontSize: 26, color: 'var(--ok)' }}>
                {r.totals.accepted}
              </p>
            </div>
            <div className="card">
              <p className="xs muted">Recusados</p>
              <p className="fr tab" style={{ fontSize: 26, color: 'var(--err)' }}>
                {r.totals.declined}
              </p>
            </div>
            <div className="card">
              <p className="xs muted">Expirados</p>
              <p className="fr tab" style={{ fontSize: 26 }}>
                {r.totals.expired}
              </p>
            </div>
            <div className="card">
              <p className="xs muted">Aguardando resposta</p>
              <p className="fr tab" style={{ fontSize: 26, color: 'var(--info)' }}>
                {r.totals.open}
              </p>
            </div>
            <div
              className="card"
              style={{ background: 'var(--pe-soft)', borderColor: 'transparent' }}
            >
              <p className="xs muted">Conversão</p>
              <p className="fr tab" style={{ fontSize: 26 }}>
                {pct(r.conversionBps, 0)}
              </p>
              <p className="xs muted">
                {r.totals.accepted} de {r.totals.sent}
                {r.totals.open
                  ? ` · ${r.totals.open} ainda ${r.totals.open === 1 ? 'aberto' : 'abertos'}`
                  : ''}
              </p>
            </div>
          </div>
          <div className="legend">
            <span>
              <i className="sw" style={{ background: FUNNEL_COLORS.accepted }} />
              Aceito
            </span>
            <span>
              <i className="sw" style={{ background: FUNNEL_COLORS.declined }} />
              Recusado
            </span>
            <span>
              <i className="sw" style={{ background: FUNNEL_COLORS.expired }} />
              Expirado
            </span>
            <span>
              <i className="sw" style={{ background: FUNNEL_COLORS.open }} />
              Aguardando
            </span>
          </div>
          <div className="two">
            <FunnelRows title="Por canal" rows={r.byChannel} />
            <FunnelRows title="Por faixa de preço" rows={r.byBand} />
          </div>
          <div className="row wrap" style={{ gap: 10 }}>
            <Link className="btn sm" to="/orcamentos">
              Abrir lista de orçamentos
            </Link>
            {r.expiringSoon ? (
              <span className="sm muted">
                {r.expiringSoon.number} expira em {formatIsoDate(r.expiringSoon.validUntil)}
                {r.expiringSoon.customer
                  ? `: vale um lembrete para ${r.expiringSoon.customer}.`
                  : '.'}
              </span>
            ) : null}
          </div>
        </div>
      )}
    </Loading>
  );
}
